import { createContext, useCallback, useContext } from 'react'

/**
 * Per-firm column names for the 21 SPECIALIZED report pages (CC-50291).
 *
 * The 119 generic reports get aliases from one transform on the report
 * definition in `ReportView` — every surface downstream reads `col.label` off
 * the same objects. The specialized pages never pass through that: `ReportView`
 * hands off entirely to the registered component, and each page derives its own
 * columns from its own payload.
 *
 * This bridges that gap **without touching any of the 20 page files**, by
 * wrapping the ONE hand-off point (`ReportView`'s `<Specialized />`) — which
 * already knows the `actionKey` and the report id — and letting the two shared
 * grids consume it. Same idea, and the same shape, as `reportExportViewContext`
 * (CC-49594), which threads the export view up from a grid to the shell without
 * touching the pages either.
 *
 * ## It is BIDIRECTIONAL, on purpose
 *
 * Down: `aliases`, so a grid can rename what it renders.
 * Up:  `publishColumns`, so the grid reports the columns it actually derived.
 *
 * The upward half is what makes the feature usable rather than merely visible.
 * A specialized report's columns exist only inside its grid — the generic
 * definition carries none (that is CC-50275's root cause) — so without the grid
 * telling us, an admin would have nothing to rename and aliases here would be
 * display-only and permanently empty. Inert-but-shipped is the exact outcome
 * CC-50265 was held back to avoid.
 *
 * ## Column identity
 *
 * The alias key is the grid's own STABLE key, never a positional index:
 *   CsvReportGrid  → the CSV header name (or `col{i}` for a blank/duplicate)
 *   AutoReportGrid → the row key
 * Both are what the backend's `column_id` was specified to hold for these
 * reports. A positional id would re-point every alias below a hidden or
 * reordered column at the wrong one — a mislabelled figure on a financial
 * report, not a cosmetic slip.
 *
 * Hooks and the context live here (a plain `.js`, no JSX) so the provider file
 * can export only its component — `react-refresh/only-export-components`, the
 * same split `reportExportViewContext.js` uses.
 */
export const ColumnAliasContext = createContext(null)

export const useColumnAliasContext = () => useContext(ColumnAliasContext)

/**
 * Resolve one column's display label. Returns CB's own label unless the firm
 * typed something — cb2 never invents a label, and a blank alias is the absence
 * of one, not a label (see `applyColumnAliases`).
 *
 * Safe outside a provider: a grid rendered on a page with no alias context
 * (or with the feature flag off) gets CB's labels, exactly as before.
 */
export const useAliasedLabel = () => {
  const ctx = useColumnAliasContext()
  return useCallback(
    (columnKey, fallbackLabel) => {
      const alias = ctx?.aliases?.[String(columnKey)]
      return typeof alias === 'string' && alias.trim() ? alias : fallbackLabel
    },
    [ctx]
  )
}

/**
 * Publish the columns this grid derived, so the shell can offer "Rename
 * columns" against real ones. `columns` is `[{ key, label }]` where `label` is
 * CB's own text, NOT the aliased one — the editor has to show the admin what
 * Centerbase calls it alongside what the firm calls it.
 *
 * Returns undefined outside a provider, so callers optional-call it.
 */
export const usePublishAliasColumns = () => {
  const ctx = useColumnAliasContext()
  return ctx?.publishColumns
}
